import { forwardRef, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Input } from './Input';
import { Button } from './Button';

// AddressField — the DS control for a FormSchema-v2 / domain-pack `gps` field
// (story 3-1b, P03 §7.1). Promoted into the design system rather than living
// next to the Sites form, per D44: `components/properties/AddressAutocomplete`
// was a one-off with raw `<input>`s (a G-22 reject) whose ONLY consumer was the
// page this story replaces, so it is retired here rather than copied.
//
// ── What this primitive adds over the thing it replaces ───────────────────
// P03 §7.1 requires an address/GPS lookup FAILURE to "preserve the user's input
// and expose an actionable state". The old component's catch block was
// literally `/* aborted or offline — keep silent */`: a user whose lookup
// failed saw an autocomplete that simply never suggested anything, with no way
// to know whether to keep waiting. Here a failure sets a VISIBLE, non-blocking
// notice with a Retry control, and the manual fields stay editable throughout —
// a lookup is an accelerator, never a gate. Nothing the user typed is ever
// cleared by a failure, and a suggestion only fills fields the user left empty
// or that it can improve, never silently overwriting a typed line1.
//
// ── a11y ──────────────────────────────────────────────────────────────────
// The suggestion list is a real `role="listbox"` with `aria-activedescendant`
// and ArrowUp/ArrowDown/Enter/Escape handling on the input (combobox pattern),
// so it is fully operable without a pointer; the notice is `role="status"`
// (polite — the user is mid-typing and must not be interrupted).

export interface AddressValue {
  line1: string;
  line2?: string | null | undefined;
  city: string;
  state: string;
  postcode: string;
  country: string;
  lat?: number | null | undefined;
  lng?: number | null | undefined;
}

export interface AddressFieldProps {
  id?: string;
  name: string;
  value: Partial<AddressValue> | undefined;
  onChange: (next: AddressValue) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Autocomplete provider. Injected so the primitive has no network opinion
   * of its own and the gallery/tests can drive both outcomes deterministically. */
  lookup?: (query: string) => Promise<AddressSuggestion[]>;
  'aria-describedby'?: string;
  'aria-required'?: true;
}

export interface AddressSuggestion {
  id: string;
  label: string;
  address: Partial<AddressValue>;
}

const MAPBOX_TOKEN: string = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) ?? '';

const EMPTY: AddressValue = { line1: '', line2: '', city: '', state: '', postcode: '', country: 'Australia' };

/** Map a Mapbox Geocoding feature onto the contract's GeoPoint shape. */
function toAddress(feature: {
  text?: string;
  address?: string;
  center?: [number, number];
  context?: Array<{ id: string; text: string }>;
}): Partial<AddressValue> {
  const ctx = (id: string) => feature.context?.find((c) => c.id.startsWith(id));
  return {
    line1: [feature.address, feature.text].filter(Boolean).join(' '),
    city: ctx('place')?.text ?? ctx('locality')?.text ?? '',
    state: ctx('region')?.text ?? '',
    postcode: ctx('postcode')?.text ?? '',
    country: ctx('country')?.text ?? '',
    ...(feature.center ? { lng: feature.center[0], lat: feature.center[1] } : {}),
  };
}

/** The default provider: Mapbox geocoding. Throws on any non-OK response so
 * the caller can show the failure state instead of an empty list that is
 * indistinguishable from "no matches". */
export async function mapboxAddressLookup(query: string, signal?: AbortSignal): Promise<AddressSuggestion[]> {
  if (!MAPBOX_TOKEN) return [];
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
    `?access_token=${MAPBOX_TOKEN}&autocomplete=true&types=address&limit=5`;
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) throw new Error(`address lookup failed (${res.status})`);
  const body = (await res.json()) as {
    features?: Array<{ id: string; place_name: string } & Parameters<typeof toAddress>[0]>;
  };
  return (body.features ?? []).map((f) => ({ id: f.id, label: f.place_name, address: toAddress(f) }));
}

export const AddressField = forwardRef<HTMLInputElement, AddressFieldProps>(function AddressField(
  { id, name, value, onChange, onBlur, disabled, invalid, lookup, ...aria },
  ref,
) {
  const current: AddressValue = { ...EMPTY, ...(value ?? {}) };
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastQueryRef = useRef('');

  // G-23: a primitive that owns a timer + an AbortController MUST clean both
  // up, or a form unmounted mid-lookup leaves a pending setState behind.
  useEffect(
    () => () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  const runLookup = (query: string) => {
    lastQueryRef.current = query;
    if (query.trim().length < 4) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setPending(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const provider = lookup ?? ((q: string) => mapboxAddressLookup(q, controller.signal));
    provider(query)
      .then((results) => {
        if (controller.signal.aborted) return;
        setSuggestions(results);
        setActive(-1);
        setOpen(results.length > 0);
        setLookupFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // The actionable state P03 §7.1 asks for. The typed value is untouched.
        setSuggestions([]);
        setOpen(false);
        setLookupFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPending(false);
      });
  };

  const scheduleLookup = (query: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runLookup(query), 300);
  };

  const patch = (next: Partial<AddressValue>) => onChange({ ...current, ...next });

  const applySuggestion = (suggestion: AddressSuggestion) => {
    const a = suggestion.address;
    // Only fill what the suggestion actually resolved — a partial result
    // (P03 §7.1's "partial data") must not blank fields the user already typed.
    onChange({
      ...current,
      ...(a.line1 ? { line1: a.line1 } : {}),
      ...(a.city ? { city: a.city } : {}),
      ...(a.state ? { state: a.state } : {}),
      ...(a.postcode ? { postcode: a.postcode } : {}),
      ...(a.country ? { country: a.country } : {}),
      ...(typeof a.lat === 'number' ? { lat: a.lat } : {}),
      ...(typeof a.lng === 'number' ? { lng: a.lng } : {}),
    });
    setOpen(false);
    setActive(-1);
  };

  const listId = `${id ?? name}-suggestions`;
  const optionId = (index: number) => `${listId}-${index}`;

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      const picked = suggestions[active];
      if (picked) applySuggestion(picked);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="space-y-2" data-testid={`address-field-${name}`}>
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          name={`${name}.line1`}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          {...(active >= 0 ? { 'aria-activedescendant': optionId(active) } : {})}
          autoComplete="off"
          value={current.line1}
          disabled={disabled ?? false}
          invalid={invalid ?? false}
          placeholder="Street address — start typing for suggestions"
          aria-label="Street address"
          onChange={(event) => {
            patch({ line1: event.target.value });
            scheduleLookup(event.target.value);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 150);
            onBlur?.();
          }}
          onKeyDown={onKeyDown}
          {...aria}
        />
        {open && suggestions.length > 0 && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Address suggestions"
            className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-border bg-background shadow-2xl"
          >
            {suggestions.map((s, index) => (
              <li key={s.id} id={optionId(index)} role="option" aria-selected={index === active}>
                <button
                  type="button"
                  className={`focus-ring block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-white/50 dark:hover:bg-white/10 ${
                    index === active ? 'bg-white/60 dark:bg-white/10' : ''
                  }`}
                  onMouseDown={(e) => e.preventDefault() /* keep input focus until the click lands */}
                  onClick={() => applySuggestion(s)}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {lookupFailed && (
        <div
          role="status"
          data-testid={`address-lookup-error-${name}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-warning bg-warning/10 px-3 py-2 text-xs text-foreground"
        >
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>Address lookup is unavailable. Your entry is kept — fill the fields below manually, or retry.</span>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={pending}
            onClick={() => runLookup(lastQueryRef.current || current.line1)}
          >
            {pending ? 'Retrying…' : 'Retry lookup'}
          </Button>
        </div>
      )}

      <Input
        name={`${name}.line2`}
        type="text"
        value={current.line2 ?? ''}
        disabled={disabled ?? false}
        aria-label="Address line 2"
        placeholder="Apartment, suite, etc. (optional)"
        onChange={(event) => patch({ line2: event.target.value })}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <Input
          name={`${name}.city`}
          type="text"
          value={current.city}
          disabled={disabled ?? false}
          invalid={invalid ?? false}
          aria-label="City"
          placeholder="City"
          onChange={(event) => patch({ city: event.target.value })}
        />
        <Input
          name={`${name}.state`}
          type="text"
          value={current.state}
          disabled={disabled ?? false}
          invalid={invalid ?? false}
          aria-label="State"
          placeholder="State"
          onChange={(event) => patch({ state: event.target.value })}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input
          name={`${name}.postcode`}
          type="text"
          value={current.postcode}
          disabled={disabled ?? false}
          invalid={invalid ?? false}
          aria-label="Postcode"
          placeholder="Postcode"
          onChange={(event) => patch({ postcode: event.target.value })}
        />
        <Input
          name={`${name}.country`}
          type="text"
          value={current.country}
          disabled={disabled ?? false}
          aria-label="Country"
          placeholder="Country"
          onChange={(event) => patch({ country: event.target.value })}
        />
      </div>
    </div>
  );
});
